import React, { lazy, Suspense } from 'react';
import {
    BrowserRouter,
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useParams,
} from 'react-router-dom';
import Start from './Views/Home/Start';
import CookieConsent from './Components/CookieConsent';
import { UtmUtils } from './Utils/UtmTracker';
import {
    buildLocalizedPathWithQueryAndHash,
    isSupportedLocale,
    resolveLocaleForMissingPath,
} from './i18n/config';
import { I18nProvider, useI18n } from './i18n/I18nProvider';

const Upload = lazy(() => import('./Views/Home/Upload'));
const HeicToJpegConverter = lazy(
    () => import('./Views/Tools/HeicToJpegConverter'),
);

const HIGH_INTENT_PAGES = [
    {
        path: 'share-photos-from-iphone-to-pc',
        titleKey: 'highIntent.sharePhotosIphonePc.title',
        subtitleKey: 'highIntent.sharePhotosIphonePc.subtitle',
        ctaKey: 'highIntent.sharePhotosIphonePc.cta',
    },
    {
        path: 'airdrop-for-windows',
        titleKey: 'highIntent.airdropWindows.title',
        subtitleKey: 'highIntent.airdropWindows.subtitle',
        ctaKey: 'highIntent.airdropWindows.cta',
    },
    {
        path: 'transfer-iphone-to-pc-without-itunes',
        titleKey: 'highIntent.transferIphoneToPcWithoutItunes.title',
        subtitleKey: 'highIntent.transferIphoneToPcWithoutItunes.subtitle',
        ctaKey: 'highIntent.transferIphoneToPcWithoutItunes.cta',
    },
    {
        path: 'send-photos-from-iphone-to-computer',
        titleKey: 'highIntent.sendPhotosIphoneToComputer.title',
        subtitleKey: 'highIntent.sendPhotosIphoneToComputer.subtitle',
        ctaKey: 'highIntent.sendPhotosIphoneToComputer.cta',
    },
    {
        path: 'send-photos-from-android-to-computer',
        titleKey: 'highIntent.sendPhotosAndroidToComputer.title',
        subtitleKey: 'highIntent.sendPhotosAndroidToComputer.subtitle',
        ctaKey: 'highIntent.sendPhotosAndroidToComputer.cta',
    },
    {
        path: 'send-photos-from-android-to-pc',
        titleKey: 'highIntentSeo.sendPhotosAndroidToPc.title',
        subtitleKey: 'highIntentSeo.sendPhotosAndroidToPc.intro',
        ctaKey: 'highIntent.sendPhotosAndroidToComputer.cta',
    },
    {
        path: 'send-large-photos-from-iphone',
        titleKey: 'highIntent.sendLargePhotosIphone.title',
        subtitleKey: 'highIntent.sendLargePhotosIphone.subtitle',
        ctaKey: 'highIntent.sendLargePhotosIphone.cta',
    },
    {
        path: 'send-apk-files-from-computer-to-android',
        titleKey: 'highIntent.sendApkComputerToAndroid.title',
        subtitleKey: 'highIntent.sendApkComputerToAndroid.subtitle',
        ctaKey: 'highIntent.sendApkComputerToAndroid.cta',
    },
    {
        path: 'get-large-files-from-iphone',
        titleKey: 'highIntent.getLargeFilesFromIphone.title',
        subtitleKey: 'highIntent.getLargeFilesFromIphone.subtitle',
        ctaKey: 'highIntent.getLargeFilesFromIphone.cta',
    },
    {
        path: 'get-large-videos-from-iphone',
        titleKey: 'highIntent.getLargeVideosFromIphone.title',
        subtitleKey: 'highIntent.getLargeVideosFromIphone.subtitle',
        ctaKey: 'highIntent.getLargeVideosFromIphone.cta',
    },
] as const;

function LocalizedStart({
    titleKey = 'home.title',
    subtitleKey = 'home.subtitle',
    ctaKey = 'home.cta',
    descriptionKey,
}: {
    titleKey?: string;
    subtitleKey?: string;
    ctaKey?: string;
    descriptionKey?: string;
}) {
    const { t } = useI18n();

    return (
        <Start
            titleLabel={t(titleKey)}
            subtitleLabel={t(subtitleKey)}
            ctaLabel={t(ctaKey)}
            descriptionLabel={descriptionKey ? t(descriptionKey) : undefined}
            descriptionSuffixLabel={t('meta.startDescriptionSuffix')}
        />
    );
}

function MissingLocaleRedirect() {
    const location = useLocation();

    React.useEffect(() => {
        const resolvedLocale = resolveLocaleForMissingPath();
        const localizedUrl = buildLocalizedPathWithQueryAndHash(
            location.pathname,
            location.search,
            location.hash,
            resolvedLocale,
        );

        window.location.replace(localizedUrl);
    }, [location.hash, location.pathname, location.search]);

    return null;
}

function LocaleShell() {
    const { lng } = useParams();
    const normalizedLocale = lng?.toLowerCase();

    if (!isSupportedLocale(normalizedLocale)) {
        return <MissingLocaleRedirect />;
    }

    return (
        <I18nProvider locale={normalizedLocale}>
            <Outlet />
            <CookieConsent />
        </I18nProvider>
    );
}

function LocalizedNotFound() {
    const { lng } = useParams();
    const locale = lng?.toLowerCase();

    return (
        <Navigate
            to={isSupportedLocale(locale) ? `/${locale}` : '/en'}
            replace
        />
    );
}

function App() {
    React.useEffect(() => {
        UtmUtils.captureFromUrl();
    }, []);

    return (
        <BrowserRouter>
            <Suspense
                fallback={
                    <div className="flex items-center justify-center min-h-screen bg-[#f2f2f6] text-gray-500 font-sans">
                        Loading...
                    </div>
                }
            >
                <Routes>
                    <Route path="/:lng" element={<LocaleShell />}>
                        <Route
                            index
                            element={
                                <LocalizedStart descriptionKey="home.description" />
                            }
                        />

                        {HIGH_INTENT_PAGES.map((page) => (
                            <Route
                                key={page.path}
                                path={page.path}
                                element={
                                    <LocalizedStart
                                        titleKey={page.titleKey}
                                        subtitleKey={page.subtitleKey}
                                        ctaKey={page.ctaKey}
                                    />
                                }
                            />
                        ))}

                        <Route path="send" element={<Upload />} />
                        <Route
                            path="heic-to-jpeg-converter"
                            element={<HeicToJpegConverter />}
                        />

                        <Route path="*" element={<LocalizedNotFound />} />
                    </Route>

                    <Route path="*" element={<MissingLocaleRedirect />} />
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
}

export default App;
